import React, { useEffect, useState } from "react";
import CustomDropdown from "../common/CustomDropdown";
import { searchService, tenderService } from "../../services/api";
import {
  FEED_TYPE_FILTER_CHOICES,
  FEED_STATUS_FILTER_CHOICES,
  CONTINENT_CHOICES,
  DEFAULT_FEED_FILTERS,
  getPositionsForCategory,
} from "../../constants/agentTenderChoices";

// Feed filter modal for /agent-tenders. Reaches full parity with the club
// TenderFilterModal (same param keys) plus the verified-only toggle (G4 in
// API-CONTRACT). Country/tier filters are club-derived on the backend and never
// resolve the club. Country/division/nationality lists come from the same live
// endpoints the club modal uses; if they can't load (e.g. mock harness), the
// modal degrades to the static category/continent lists.
const AgentTenderFilterModal = ({ isOpen, onClose, onApply, initialFilters }) => {
  const [filters, setFilters] = useState(initialFilters);
  const [countries, setCountries] = useState([]);
  const [tenderFilters, setTenderFilters] = useState({
    countries: [],
    divisions: [],
    positions: [],
    continents: [],
  });

  useEffect(() => {
    if (isOpen) setFilters(initialFilters);
  }, [isOpen, initialFilters]);

  // Full country list (Nationality dropdown + flag lookup).
  useEffect(() => {
    if (!isOpen) return;
    (async () => {
      try {
        const res = await searchService.getCountries();
        if (res.success && res.data) setCountries(res.data);
      } catch (e) {
        /* offline / mock — Nationality falls back to empty, non-blocking */
      }
    })();
  }, [isOpen]);

  // Consolidated tender-filter options (countries / divisions), cascading on the
  // chosen country/continent, matching the club modal.
  useEffect(() => {
    if (!isOpen) return undefined;
    const controller = new AbortController();
    (async () => {
      try {
        const res = await tenderService.getTenderFilters({
          country: filters.country || "",
          continent: filters.continent || "",
          tender_category:
            filters.tender_category === "staff" ? "staffs" : "players",
          signal: controller.signal,
        });
        if (res.success && res.data) setTenderFilters(res.data);
      } catch (e) {
        if (e?.name !== "CanceledError") {
          /* offline / mock — Country/Division fall back to empty, non-blocking */
        }
      }
    })();
    return () => controller.abort();
  }, [isOpen, filters.country, filters.continent, filters.tender_category]);

  if (!isOpen) return null;

  const set = (key, value) => setFilters((prev) => ({ ...prev, [key]: value }));

  const positionOptions = [
    { value: "", label: "All positions" },
    ...getPositionsForCategory(filters.tender_category),
  ];

  const continentOptions =
    tenderFilters.continents.length > 0
      ? [
          { value: "", label: "All continents" },
          ...tenderFilters.continents.map((c) => ({
            value: c.name,
            label: `${c.name} (${c.tender_count})`,
          })),
        ]
      : CONTINENT_CHOICES;

  const countryOptions =
    tenderFilters.countries.length > 0
      ? [
          { value: "", label: "All countries" },
          ...tenderFilters.countries.map((c) => ({
            value: c.name,
            label: `${c.name} (${c.tender_count})`,
            iconClassName: c.code ? `fi fi-${c.code}` : undefined,
          })),
        ]
      : [{ value: "", label: "All countries" }];

  const divisionOptions = [
    { value: "", label: "All divisions" },
    ...tenderFilters.divisions.map((d) => ({
      value: d.name,
      label: d.country ? `${d.name} — ${d.country} (${d.tender_count})` : `${d.name} (${d.tender_count})`,
    })),
  ];

  const nationalityOptions = [
    { value: "", label: "All nationalities" },
    ...countries.map((c) => ({
      value: c.name,
      label: c.name,
      iconClassName: c.country_code ? `fi fi-${c.country_code}` : undefined,
      image: c.image_url,
    })),
  ];

  const handleApply = () => {
    onApply(filters);
    onClose();
  };

  // Reset local state only; the user still confirms with "Apply filters"
  // (matches the club TenderFilterModal's clear-then-apply semantics).
  const handleReset = () => setFilters(DEFAULT_FEED_FILTERS);

  const Field = ({ label, children }) => (
    <div className="mb-5">
      <h3 className="text-sm font-medium mb-2">{label}</h3>
      {children}
    </div>
  );

  const dropdownCls = "w-full px-3 py-2 border rounded-lg";

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-end sm:items-center justify-center z-50">
      <div className="bg-white rounded-t-2xl sm:rounded-lg p-6 w-full sm:max-w-lg max-h-[90vh] overflow-y-auto">
        <div className="flex justify-between items-start mb-4">
          <h2 className="text-xl font-semibold">Filter requests</h2>
          <button onClick={onClose} className="text-secondary hover:text-gray-900">
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Category is owned by the feed's Players/Coaches/Staffs/Others pills
            now (P1) — removed here to avoid a conflicting second control. */}

        <Field label="Market value">
          <div className="flex gap-3">
            <input
              type="number"
              value={filters.market_value_from || ""}
              onChange={(e) => set("market_value_from", e.target.value)}
              placeholder="Min value"
              className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-1 focus:ring-blue-500"
            />
            <input
              type="number"
              value={filters.market_value_to || ""}
              onChange={(e) => set("market_value_to", e.target.value)}
              placeholder="Max value"
              className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-1 focus:ring-blue-500"
            />
          </div>
        </Field>

        <Field label="Continent">
          <CustomDropdown
            options={continentOptions}
            value={filters.continent || ""}
            onChange={(v) => set("continent", v)}
            placeholder="All continents"
            displayProperty="label"
            valueProperty="value"
            searchEnabled
            searchPlaceholder="Search continent..."
            className={dropdownCls}
            dropdownClassName="rounded-[9px] border-[#E2E8F0]"
            optionClassName="!px-3 !py-2 !text-[13px] !font-medium"
          />
        </Field>

        <Field label="Country">
          <CustomDropdown
            options={countryOptions}
            value={filters.country || ""}
            onChange={(v) => set("country", v)}
            placeholder="All countries"
            displayProperty="label"
            valueProperty="value"
            searchEnabled
            searchPlaceholder="Search country..."
            className={dropdownCls}
            dropdownClassName="rounded-[9px] border-[#E2E8F0]"
            optionClassName="!px-3 !py-2 !text-[13px] !font-medium"
          />
        </Field>

        <Field label="Division">
          <CustomDropdown
            options={divisionOptions}
            value={filters.division || ""}
            onChange={(v) => set("division", v)}
            placeholder="All divisions"
            displayProperty="label"
            valueProperty="value"
            searchEnabled
            searchPlaceholder="Search division..."
            className={dropdownCls}
            dropdownClassName="rounded-[9px] border-[#E2E8F0]"
            optionClassName="!px-3 !py-2 !text-[13px] !font-medium"
          />
        </Field>

        <Field label="Position">
          <CustomDropdown
            options={positionOptions}
            value={filters.position || ""}
            onChange={(v) => set("position", v)}
            placeholder="All positions"
            displayProperty="label"
            valueProperty="value"
            searchEnabled
            searchPlaceholder="Search position..."
            className={dropdownCls}
            dropdownClassName="rounded-[9px] border-[#E2E8F0]"
            optionClassName="!px-3 !py-2 !text-[13px] !font-medium"
          />
        </Field>

        <Field label="Nationality">
          <CustomDropdown
            options={nationalityOptions}
            value={filters.nationality || ""}
            onChange={(v) => set("nationality", v)}
            placeholder="All nationalities"
            displayProperty="label"
            valueProperty="value"
            searchEnabled
            searchPlaceholder="Search nationality..."
            className={dropdownCls}
            dropdownClassName="rounded-[9px] border-[#E2E8F0]"
            optionClassName="!px-3 !py-2 !text-[13px] !font-medium"
          />
        </Field>

        <Field label="Age range">
          <div className="flex gap-3">
            <input
              type="number"
              value={filters.min_age || ""}
              onChange={(e) => set("min_age", e.target.value)}
              placeholder="Min"
              min="12"
              max="50"
              className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-1 focus:ring-blue-500"
            />
            <input
              type="number"
              value={filters.max_age || ""}
              onChange={(e) => set("max_age", e.target.value)}
              placeholder="Max"
              min="12"
              max="50"
              className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-1 focus:ring-blue-500"
            />
          </div>
        </Field>

        <Field label="Request type">
          <CustomDropdown
            options={FEED_TYPE_FILTER_CHOICES}
            value={filters.type_of_tender || ""}
            onChange={(v) => set("type_of_tender", v)}
            placeholder="All types"
            displayProperty="label"
            valueProperty="value"
            className={dropdownCls}
            dropdownClassName="rounded-[9px] border-[#E2E8F0]"
            optionClassName="!px-3 !py-2 !text-[13px] !font-medium"
          />
        </Field>

        {/* Recruiting for (Professional/Youth) is owned by the feed toolbar's
            level dropdown now (P1) — removed here to avoid a duplicate. */}

        <Field label="Status">
          <CustomDropdown
            options={FEED_STATUS_FILTER_CHOICES}
            value={filters.status || "all"}
            onChange={(v) => set("status", v)}
            placeholder="All status"
            displayProperty="label"
            valueProperty="value"
            className={dropdownCls}
            dropdownClassName="rounded-[9px] border-[#E2E8F0]"
            optionClassName="!px-3 !py-2 !text-[13px] !font-medium"
          />
        </Field>

        {/* Sort is owned by the feed toolbar's sort dropdown now (P8) —
            removed here to avoid a duplicate. */}

        <label className="flex items-center gap-2 mb-6">
          <input
            type="checkbox"
            checked={!!filters.verified}
            onChange={(e) => set("verified", e.target.checked)}
            className="rounded"
          />
          <span className="text-sm">Show club-verified only</span>
        </label>

        <div className="flex gap-3">
          <button
            onClick={handleReset}
            className="px-4 py-2 border border-gray-300 text-gray-600 rounded-lg hover:bg-gray-50"
          >
            Reset
          </button>
          <button onClick={onClose} className="flex-1 px-4 py-2 border rounded-lg hover:bg-gray-50">
            Cancel
          </button>
          <button
            onClick={handleApply}
            className="flex-1 px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600"
          >
            Apply filters
          </button>
        </div>
      </div>
    </div>
  );
};

export default AgentTenderFilterModal;
